import { LayerHost } from '@fluentui/react';
import {
  BookTimeDialog,
  ClassNamesFromIStyles,
  Footer,
  Loader,
  loaderStylesSpinnerXLarge,
  NotificationProvider,
  Toast,
  useAppNavigationContext,
  useClassNames,
} from '@h2oai/ui-kit';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { RoleProvider } from '../../authz/providers/RoleProvider';
import { UsersProvider } from '../../authz/providers/UsersProvider';
import { BannerBar } from '../../components/BannerBar/BannerBar';
import { type ILeftPanelProps, LeftPanel } from '../../components/LeftPanel/LeftPanel';
import { Navigation } from '../../components/Navigation/Navigation';
import { LeftPanelProvider } from '../../utils/contexts';
import { useEnv, useUser } from '../../utils/hooks';
import { ConfirmDialogProvider } from '../MLOps/ConfirmDialogProvider';
import { WorkspaceProvider } from '../Orchestrator/WorkspaceProvider';
import { RoutePaths } from '../RoutePaths';
import { Routes } from '../Routes';
import { useNotices, useOsanoScript } from './hooks';
import { IProtectedStyles, protectedStylesDefault } from './Protected.styles';

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[] | undefined;
    Osano?: {
      cm: {
        showDrawer: (dialogId: string) => void;
      };
    };
  }
}

export const Protected = () => {
  const { navigationSize } = useAppNavigationContext();
  const refMain = useRef<HTMLElement>(null);
  const location = useLocation();
  const env = useEnv();
  const user = useUser();

  const [leftPanelProps, setLeftPanelProps] = useState<ILeftPanelProps | undefined>();
  const [bookTimeDialogHidden, setBookTimeDialogHidden] = useState<boolean>(true);

  const onDismissBookTimeDialog = useCallback(() => setBookTimeDialogHidden(true), []);
  const onClickBookTime = useCallback(() => setBookTimeDialogHidden(false), []);

  // Load environment notices and book time notification
  useNotices(env, onClickBookTime);

  // Load Osano cookie consent script
  const osanoIsLoaded = useOsanoScript(env);

  // Scroll to top on appstore navigation
  useEffect(() => {
    if (refMain.current && location.pathname === RoutePaths.APPSTORE) {
      refMain.current.scrollTo(0, 0);
    }
  }, [location.pathname, location.search]);

  const classNames = useClassNames<IProtectedStyles, ClassNamesFromIStyles<IProtectedStyles>>(
    'Protected',
    protectedStylesDefault
  );

  return !user ? (
    <Loader styles={loaderStylesSpinnerXLarge} label="Loading..." />
  ) : (
    <WorkspaceProvider>
      <UsersProvider>
        <RoleProvider>
          <NotificationProvider>
            <ConfirmDialogProvider>
              <LeftPanelProvider setLeftPanelProps={setLeftPanelProps}>
                <div className={classNames.root}>
                  <Navigation />
                  <Toast />
                  <main
                    className={
                      leftPanelProps?.content
                        ? `${classNames.main} ${leftPanelProps.className || ''} has-left-panel`
                        : classNames.main
                    }
                    data-test="main-content"
                    ref={refMain}
                    style={{ marginLeft: navigationSize }}
                  >
                    <div className={classNames.contents}>
                      <BannerBar />
                      <div className={classNames.contentsBody}>
                        <LeftPanel {...leftPanelProps} />
                        <Routes />
                      </div>
                    </div>
                    <Footer
                      onCookiePreferencesClick={
                        osanoIsLoaded ? () => window.Osano?.cm.showDrawer('osano-cm-dom-info-dialog-open') : undefined
                      }
                      onPrivacyPolicyClick={() => window.open('https://h2o.ai/legal/privacy')}
                    />
                    <BookTimeDialog
                      url={env?.menu?.bookTimeLink}
                      onDismiss={onDismissBookTimeDialog}
                      hidden={bookTimeDialogHidden}
                    />
                    <LayerHost id="main" className={classNames.layerHost} style={{ marginLeft: navigationSize }} />
                  </main>
                </div>
              </LeftPanelProvider>
            </ConfirmDialogProvider>
          </NotificationProvider>
        </RoleProvider>
      </UsersProvider>
    </WorkspaceProvider>
  );
};
